import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { Send, Star } from "lucide-react";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [quote, setQuote] = useState({ amount: "", note: "" });
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const bottomRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/bookings/${id}`);
    setB(data);
    const { data: msgs } = await api.get(`/bookings/${id}/messages`);
    setMessages(msgs);
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post(`/bookings/${id}/messages`, { text });
    setText("");
    load();
  };

  const sendQuote = async () => {
    if (!quote.amount) return toast.error("Enter an amount");
    await api.post(`/bookings/${id}/quote`, { amount: parseFloat(quote.amount), note: quote.note });
    toast.success("Quote sent");
    load();
  };

  const accept = async () => { await api.post(`/bookings/${id}/accept`); toast.success("Accepted"); load(); };
  const decline = async () => { await api.post(`/bookings/${id}/decline`); load(); };
  const complete = async () => { try { await api.post(`/bookings/${id}/complete`); toast.success("Marked complete"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const pay = async () => {
    try {
      const { data } = await api.post(`/payments/checkout`, { booking_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/bookings/${id}/review`, review);
      toast.success("Thanks for your review!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!b) return (<div className="min-h-screen bg-white"><Navbar /><div className="p-8">Loading…</div></div>);

  const isProvider = user?.id === b.provider_user_id;
  const isCustomer = user?.id === b.customer_id;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-4xl mx-auto px-4 py-8 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 card">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-2xl font-bold">Booking</h1>
            <StatusBadge status={b.status} />
          </div>
          <div className="mt-4 text-sm text-slate-600 space-y-1">
            <div><b>Provider:</b> {b.provider_name}</div>
            <div><b>Customer:</b> {b.customer_name}</div>
            <div><b>Postcode:</b> {b.postcode}</div>
            <div><b>Date:</b> {b.preferred_date} · {b.preferred_time}</div>
            {b.budget && <div><b>Budget:</b> £{b.budget}</div>}
          </div>
          <p className="mt-4 text-slate-800">{b.description}</p>
          {b.photos?.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {b.photos.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-20 rounded-lg object-cover" />)}
            </div>
          )}

          {b.quote_amount && (
            <div className="mt-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
              <div className="font-semibold text-emerald-800">Quote: £{b.quote_amount.toFixed(2)}</div>
              {b.quote_note && <div className="text-sm text-emerald-800/80 mt-1">{b.quote_note}</div>}
              <div className="text-xs text-emerald-700 mt-2">Provider payout £{b.provider_payout} · Platform commission £{b.commission_amount} ({b.commission_pct}%)</div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            {isProvider && b.status === "requested" && (
              <>
                <div className="w-full grid grid-cols-3 gap-2 items-end">
                  <div>
                    <label className="label">Quote £</label>
                    <input data-testid="quote-amount" type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} className="field" />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Note</label>
                    <input data-testid="quote-note" value={quote.note} onChange={(e) => setQuote({ ...quote, note: e.target.value })} className="field" />
                  </div>
                </div>
                <button data-testid="quote-send" onClick={sendQuote} className="btn-accent">Send quote</button>
                <button data-testid="quote-decline" onClick={decline} className="btn-outline">Decline</button>
              </>
            )}
            {isCustomer && b.status === "quoted" && (
              <>
                <button data-testid="quote-accept" onClick={accept} className="btn-accent">Accept quote</button>
                <button data-testid="quote-reject" onClick={decline} className="btn-outline">Decline</button>
              </>
            )}
            {isCustomer && b.status === "accepted" && b.payment_status !== "paid" && (
              <button data-testid="pay-now" onClick={pay} className="btn-accent">Pay £{b.quote_amount?.toFixed(2)} now</button>
            )}
            {isProvider && b.payment_status === "paid" && b.status !== "completed" && (
              <button data-testid="mark-complete" onClick={complete} className="btn-primary">Mark completed</button>
            )}
          </div>

          {isCustomer && b.status === "completed" && (
            <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="font-semibold text-slate-900">Leave a review</div>
              <div className="mt-2 flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} data-testid={`review-star-${n}`} onClick={() => setReview({ ...review, rating: n })}>
                    <Star size={22} className={n <= review.rating ? "text-amber-500 fill-amber-500" : "text-slate-300"} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-comment" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} className="field mt-2" rows={3} placeholder="How did it go?" />
              <button data-testid="review-submit" onClick={submitReview} className="btn-accent mt-3">Submit review</button>
            </div>
          )}
        </div>

        <div className="md:col-span-2 card flex flex-col h-[600px]">
          <div className="font-semibold text-slate-900 border-b border-slate-200 pb-3">Messages</div>
          <div className="flex-1 overflow-y-auto py-3 space-y-2">
            {messages.map((m) => (
              <div key={m.id} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.sender_id === user?.id ? "ml-auto bg-emerald-500 text-white" : "bg-slate-100 text-slate-900"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>
                {m.text}
              </div>
            ))}
            {messages.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No messages yet</div>}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={sendMsg} className="flex gap-2 pt-3 border-t border-slate-200">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} className="field" placeholder="Type a message…" />
            <button data-testid="chat-send" className="btn-accent !px-4"><Send size={16} /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
